import { PureComponent } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import Raven from 'raven-js';
import I18n from 'I18n';
import { MessageTypes } from 'video-embed/constants';
import { ConversionAssetPositions, ConversionAssetTypes, PlayerTypes } from 'video-embed/types';
import { debugLog } from 'video-embed/utils/debug';
import { isAuthedAppPreview } from 'video-embed/utils/env';
import { isWebInteractiveId } from 'video-embed/utils/overlay';
import { PlayerErrorTypes, PlayerStatus } from '../constants';
import FormModal from '../components/FormModal';
import PlayerError from '../components/PlayerError';
import VideoJSWrapper from '../components/VideoJSWrapper';
import ModalOverlay from '../components/ModalOverlay';
import { getAppError, getCaptions, getConfirmedVisible } from '../selectors';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
// Extended by EmbedContainer
export const playerContainerPropTypes = {
  embedId: PropTypes.string,
  params: PropTypes.object.isRequired,
  playerType: PropTypes.oneOf(Object.values(PlayerTypes)),
  video: PropTypes.object,
  videoFetchError: PropTypes.object,
  appError: PropTypes.oneOf(Object.values(PlayerErrorTypes)),
  utk: PropTypes.string,
  pageMeta: PropTypes.object,
  captions: PropTypes.array,
  currentTime: PropTypes.number,
  embedValidated: PropTypes.bool,
  confirmedVisible: PropTypes.bool,
  waitForConfirmedVisible: PropTypes.bool,
  onCreatePlayer: PropTypes.func.isRequired,
  postMessageToParent: PropTypes.func.isRequired,
  trackUsage: PropTypes.func.isRequired,
  trackInitialPlayUsage: PropTypes.func.isRequired
};
class PlayerContainer extends PureComponent {
  static getDerivedStateFromError() {
    return {
      hitErrorBoundary: true,
      playerStatus: PlayerStatus.ERROR
    };
  }
  constructor(props) {
    super(props);
    this.state = {
      playerStatus: PlayerStatus.INITIAL,
      hasConverted: false,
      hitErrorBoundary: false
    };
    this.postMessageToParent = (messageType, payload) => {
      this.props.postMessageToParent(messageType, payload);
    };
    this.getPlayer = () => {
      var _this$videoPlayerRef;
      return (_this$videoPlayerRef = this.videoPlayerRef) === null || _this$videoPlayerRef === void 0 ? void 0 : _this$videoPlayerRef.getPlayer();
    };
    this.withPlayer = callback => {
      const {
        embedId,
        videoFetchError,
        appError
      } = this.props;
      const player = this.getPlayer();
      if (player) {
        callback.call(this, player);
      } else {
        Raven.captureMessage('Player not ready', {
          level: 'error',
          extra: {
            embedId,
            videoFetchError,
            appError
          }
        });
      }
    };
    this.setRef = playerRef => {
      const conversionAsset = this.getConversionAsset();
      if (playerRef) {
        this.videoPlayerRef = playerRef;
        const player = playerRef.getPlayer();
        if (this.hasCustomPositionConversionAsset() && conversionAsset && typeof conversionAsset.overlayDelaySeconds === 'number') {
          player.on('timeupdate', this.handleTimeUpdate);
        }
        if (this.props.onCreatePlayer) {
          this.props.onCreatePlayer(this, player);
        }
      }
    };
    this.handleTimeUpdate = () => {
      const {
        overlayDelaySeconds
      } = this.getConversionAsset() || {};
      if (this.state.hasConverted || this.state.playerStatus !== PlayerStatus.PLAYING) {
        return;
      }
      this.withPlayer(player => {
        if (overlayDelaySeconds && player.currentTime() >= overlayDelaySeconds) {
          player.pause();
          this.handleStatusChange(PlayerStatus.CONVERSION_ASSET_OVERLAY);
          player.off('timeupdate', this.handleTimeUpdate);
        }
      });
    };
    this.handleStatusChange = (playerStatus, videoJsError) => {
      if (playerStatus === PlayerStatus.ERROR && videoJsError) {
        this.props.postMessageToParent(MessageTypes.PLAYER_ERROR, {
          errorType: videoJsError.hlsError ? videoJsError.hlsError.type : videoJsError.type || 'UNKNOWN',
          message: videoJsError.message
        });
        return;
      }
      if (this.state.playerStatus === playerStatus) {
        return;
      }
      debugLog(`Player ${this.props.embedId} status change: ${this.state.playerStatus} -> ${playerStatus}`, videoJsError);
      if (playerStatus === PlayerStatus.CONVERSION_ASSET_OVERLAY) {
        const conversionAsset = this.getConversionAsset();
        if (conversionAsset && conversionAsset.type === ConversionAssetTypes.CTA || !isAuthedAppPreview()) {
          this.postMessageToParent(MessageTypes.PLAYER_SHOW_OVERLAY, conversionAsset);
        }
        let action = conversionAsset && conversionAsset.type === ConversionAssetTypes.FORM ? 'form-displayed' : 'cta-displayed';
        if (conversionAsset && isWebInteractiveId(conversionAsset.id)) {
          action = 'cta-v2-displayed';
        }
        this.props.trackUsage('playerInteraction', {
          action
        });
      }
      this.setState({
        playerStatus,
        videoJsError
      });
    };
    this.handleReady = () => {
      this.postMessageToParent(MessageTypes.PLAYER_READY, null);
    };
    this.handlePlaying = () => {
      if (this.hasPrePositionConversionAsset() && !this.state.hasConverted) {
        setTimeout(() => {
          this.withPlayer(player => {
            player.pause();
            player.hasStarted(false);
            this.handleStatusChange(PlayerStatus.CONVERSION_ASSET_OVERLAY);
          });
        });
        return;
      }
      this.postMessageToParent(MessageTypes.PLAYER_PLAY, {
        isInitial: !this.hasPlayed
      });
      if (!this.hasPlayed) {
        this.props.trackInitialPlayUsage();
      } else {
        this.props.trackUsage('playerInteraction', {
          action: 'resume'
        });
      }
      this.hasPlayed = true;
    };
    this.handlePause = () => {
      if (this.hasPrePositionConversionAsset() && !this.state.hasConverted) {
        return;
      }
      this.postMessageToParent(MessageTypes.PLAYER_PAUSE, null);
      if (document.visibilityState === 'visible') {
        // don't duplicate the paused-for-visibility-hidden events tracked in VideoJSWrapper
        this.props.trackUsage('playerInteraction', {
          action: 'pause'
        });
      }
    };
    this.handleEnded = () => {
      this.postMessageToParent(MessageTypes.PLAYER_ENDED, null);
      if (this.hasPostPositionConversionAsset() && !this.state.hasConverted) {
        this.handleStatusChange(PlayerStatus.CONVERSION_ASSET_OVERLAY);
      }
    };
    this.handleSkipOverlay = overlayInfo => {
      const conversionAsset = this.getConversionAsset();
      let action = conversionAsset && conversionAsset.type === ConversionAssetTypes.FORM ? 'form-skipped' : 'cta-skipped';
      if (overlayInfo && overlayInfo.isLegacyForm) {
        action = `legacy-form-skipped`;
      }
      this.props.trackUsage('playerInteraction', {
        action
      });
      this.setState({
        hasConverted: true
      });
    };
    this.handlePostConversion = overlayInfo => {
      const conversionAsset = this.getConversionAsset();
      let action = conversionAsset && conversionAsset.type === ConversionAssetTypes.FORM ? 'form-submit' : 'cta-click';
      if (overlayInfo && overlayInfo.isLegacyForm) {
        action = `legacy-form-submit`;
      } else if (conversionAsset && isWebInteractiveId(conversionAsset.id)) {
        action = `cta-v2-click`;
      }
      this.props.trackUsage('playerInteraction', {
        action
      });
      this.setState({
        hasConverted: true
      });
    };
    this.hasPlayed = false;
  }
  componentDidUpdate(prevProps, prevState) {
    const {
      params,
      playerType
    } = this.props;
    if (playerType && playerType !== prevProps.playerType) {
      if (prevProps.playerType === PlayerTypes.EDITOR_PREVIEW && playerType === PlayerTypes.DEFAULT) {
        debugLog(`Changed playerType for embedId ${this.props.embedId} to ${this.state.playerType}`);
        this.withPlayer(player => {
          var _player$play;
          debugLog('Attempting play after upgrading to videojs', (_player$play = player.play()) === null || _player$play === void 0 ? void 0 : _player$play.catch(e => {
            console.error('Video playback error', e);
          }));
        });
      }
    }
    if (typeof this.props.currentTime === 'number' && prevProps.currentTime !== this.props.currentTime) {
      this.handleCurrentTimeUpdate();
    }
    if (this.state.hasConverted && !prevState.hasConverted) {
      this.withPlayer(player => {
        if (this.hasPrePositionConversionAsset() || this.hasCustomPositionConversionAsset()) {
          var _player$play2;
          (_player$play2 = player.play()) === null || _player$play2 === void 0 || _player$play2.catch(e => {
            console.error('Video playback error', e);
          });
        } else {
          // restore loop setting we have to disable for POST position form overlays in order to show the thank you message
          if (params.loop) {
            player.loop(true);
            player.currentTime(0);
          }
          this.handleStatusChange(PlayerStatus.READY);
        }
      });
    }
  }
  componentDidCatch(error, errorInfo) {
    const {
      video
    } = this.props;
    console.error('Hit error boundary', error, errorInfo);
    const extra = {
      errorInfo,
      video
    };
    Raven.captureException(error, {
      extra,
      fingerprint: ['PlayerContainer-errorBoundary']
    });
  }
  getConversionAsset() {
    const asset = this.state.conversionAsset || this.props.params.conversionAsset;
    if (asset) {
      asset.copy = {
        defaultThankYouMessage: I18n.text('videoPlayer.formModal.defaultThankYouMessage'),
        watchVideo: I18n.text('videoPlayer.formModal.inlineMessageButton')
      };
    }
    return asset;
  }
  hasPrePositionConversionAsset() {
    const {
      video
    } = this.props;
    if (video && !video.isEmbeddable) {
      return false;
    }
    const conversionAsset = this.getConversionAsset();
    return conversionAsset && conversionAsset.id && conversionAsset.position === ConversionAssetPositions.PRE;
  }
  hasPostPositionConversionAsset() {
    const {
      video
    } = this.props;
    if (video && !video.isEmbeddable) {
      return false;
    }
    const conversionAsset = this.getConversionAsset();
    return conversionAsset && conversionAsset.id && conversionAsset.position === ConversionAssetPositions.POST;
  }
  hasCustomPositionConversionAsset() {
    const {
      video
    } = this.props;
    if (video && !video.isEmbeddable) {
      return false;
    }
    const conversionAsset = this.getConversionAsset();
    return conversionAsset && conversionAsset.id && conversionAsset.position === ConversionAssetPositions.CUSTOM;
  }
  handleCurrentTimeUpdate() {
    const {
      currentTime
    } = this.props;
    if (this.hasCustomPositionConversionAsset()) {
      this.withPlayer(player => {
        const conversionAsset = this.getConversionAsset() || {};
        player.on('timeupdate', this.handleTimeUpdate);
        this.setState({
          hasConverted: false,
          conversionAsset: Object.assign({}, conversionAsset, {
            overlayDelaySeconds: currentTime
          })
        });
      });
    }
  }
  renderFormOverlay() {
    const {
      playerStatus
    } = this.state;
    const {
      video
    } = this.props;
    const {
      playButtonColor
    } = this.props.params;
    const conversionAsset = this.getConversionAsset();
    if (!conversionAsset || conversionAsset.type !== ConversionAssetTypes.FORM) {
      return null;
    }
    if (isAuthedAppPreview()) {
      return /*#__PURE__*/_jsx(FormModal, {
        player: this.getPlayer(),
        status: playerStatus,
        id: conversionAsset.id,
        video: video,
        conversionAsset: conversionAsset,
        playButtonColor: playButtonColor,
        onPostSubmit: this.handlePostConversion,
        onSkip: () => this.handleSkipOverlay()
      });
    }
    if (playerStatus === PlayerStatus.CONVERSION_ASSET_OVERLAY) {
      return /*#__PURE__*/_jsx(ModalOverlay, {
        allowClose: false,
        use: "lightbox"
      });
    }
    return null;
  }
  renderCtaOverlay() {
    const conversionAsset = this.getConversionAsset();
    const {
      playerStatus
    } = this.state;
    if (!conversionAsset || conversionAsset.type !== ConversionAssetTypes.CTA) {
      return null;
    }
    if (playerStatus !== PlayerStatus.CONVERSION_ASSET_OVERLAY) {
      return null;
    }
    return /*#__PURE__*/_jsx(ModalOverlay, {
      allowClose: false,
      use: "lightbox"
    });
  }
  renderContent() {
    const {
      params,
      playerType,
      appError,
      video,
      videoFetchError,
      pageMeta,
      currentTime,
      captions
    } = this.props;
    const {
      playerStatus,
      videoJsError
    } = this.state;
    if (appError || videoFetchError || videoJsError || playerStatus === PlayerStatus.ERROR) {
      const player = this.getPlayer();
      const hasPlaybackStarted = Boolean(player && player.currentTime() > 0);
      return /*#__PURE__*/_jsx(PlayerError, {
        appError: appError,
        videoFetchError: videoFetchError,
        videoJsError: videoJsError,
        hasPlaybackStarted: hasPlaybackStarted
      });
    }
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(VideoJSWrapper, {
        ref: this.setRef,
        embedId: this.props.embedId,
        options: params,
        video: video,
        title: video.altText || video.title,
        status: playerStatus,
        playerType: playerType,
        pageMeta: pageMeta,
        currentTime: currentTime,
        hasConverted: this.state.hasConverted,
        confirmedVisible: this.props.confirmedVisible,
        waitForConfirmedVisible: this.props.waitForConfirmedVisible,
        onChangeStatus: this.handleStatusChange,
        onReady: this.handleReady,
        onPlaying: this.handlePlaying,
        onPause: this.handlePause,
        onEnded: this.handleEnded,
        postMessageToParent: this.postMessageToParent,
        trackUsage: this.props.trackUsage,
        captions: captions
      }), this.getPlayer() && this.renderCtaOverlay(), this.getPlayer() && this.renderFormOverlay()]
    });
  }
  render() {
    const {
      playerStatus
    } = this.state;
    return (
      /*#__PURE__*/
      // @bend-expect trellis-tokenized
      _jsx("main", {
        className: "app-fullscreen-embed",
        "data-application-name": "video-player-ui",
        "data-status": playerStatus.toLowerCase(),
        children: this.renderContent()
      })
    );
  }
}
PlayerContainer.propTypes = playerContainerPropTypes;
const mapStateToProps = state => ({
  appError: getAppError(state),
  confirmedVisible: getConfirmedVisible(state),
  waitForConfirmedVisible: !isAuthedAppPreview(),
  captions: getCaptions(state)
});
export default connect(mapStateToProps)(PlayerContainer);